import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  Avatar,
  useTheme,
  useMediaQuery,
  Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { gql, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import emptyCart from "../../../assets/Icons/emptyCart.svg";
import trash from "../../../assets/Icons/trash.svg";
import useRemoveCarts from "../../../Hooks/useRemoveCart";
import useGetCart from "../../../Hooks/useGetCart";
import useIncreaseCarts from "../../../Hooks/useIncrease";
import { SlRefresh } from "react-icons/sl";
import useAddToCarts from "../../../Hooks/useAddToCarts";
import useRemoveFav from "../../../Hooks/useRemoveFav";

const GET_FAVOURITE_ITEMS = gql`
  query GetFavourite {
    favItemsVar @client
  }
`;

// Utility function to format dates
function formatDate(dateString) {
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", options);
}

const WishlistDrawer = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tabValue, setTabValue] = useState(0);

  // Authentication check
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";

  // Queries
  const { loading, data, refetch } = useQuery(GET_FAVOURITE_ITEMS, {
    fetchPolicy: "cache-first",
    errorPolicy: "ignore",
  });

  // Hooks - memoized to prevent re-creation
  const { handleGetCart } = useGetCart();
  const { handleIncreaseCart } = useIncreaseCarts();

  // Memoized cart items to prevent unnecessary re-renders
  const productCartItems = useMemo(
    () => data?.favItemsVar || [],
    [data?.favItemsVar],
  );

  // Initialize cart data only when drawer opens
  useEffect(() => {
    if (open) {
      // Add a small delay to prevent blocking UI
      const timeoutId = setTimeout(() => {
        handleGetCart();

        refetch();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  // Memoized calculations to prevent unnecessary re-computations
  const totals = useMemo(() => {
    const productTotal = productCartItems.reduce((total, item) => {
      // Use totalAmount for products (like CustomTable) or totalPrice as fallback
      const itemTotal = item.totalAmount || item.totalPrice || 0;
      return total + itemTotal;
    }, 0);

    return {
      productTotal,

      grandTotal: productTotal,
      totalItems: productCartItems.length,
    };
  }, [productCartItems]);

  const handleContinueShopping = useCallback(() => {
    onClose();
    navigate("/");
  }, [onClose, navigate]);
  const { handleAddToCart, isLoading, isSuccess } = useAddToCarts();

  const { handleRemoveFav, isLoading: removing } = useRemoveFav();

  // Memoized cart item renderer to prevent unnecessary re-renders
  const renderCartItem = useCallback(
    (item, isService = false) => (
      <ListItem
        key={item._id || item.id}
        sx={{
          flexDirection: "column",
          alignItems: "flex-start",
          border: "1px solid #f0f0f0",
          borderRadius: 2,
          mb: 1,
          p: 2,
        }}
      >
        <Box sx={{ display: "flex", width: "100%", alignItems: "center" }}>
          <Avatar
            src={item?.productOrService?.images?.[0] || item.image}
            alt={
              isService
                ? item?.service?.title
                : item?.product?.name || item.title
            }
            sx={{
              width: 60,
              height: 60,
              mr: 2,
              borderRadius: 1,
            }}
            variant="rounded"
          />

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              className="one-line-ellipsis"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                fontSize: "14px",
              }}
            >
              {item?.productOrService?.name || item.title}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "12px" }}
            >
              ${parseFloat(item?.productOrService?.price || 0).toFixed(2)}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                onClick={() => handleAddToCart(item?.productOrService?._id, 1)}
                sx={{
                  fontSize: "13px",
                  py: 0,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Add to Cart
              </Button>
              <Button
                size="small"
                onClick={() => {
                  handleRemoveFav(item?._id);
                  refetch();
                }}
                disabled={removing}
                sx={{ color: "error.main" }}
              >
                <img
                  src={trash}
                  alt="trash_icon"
                  style={{ marginRight: "3px" }}
                />
                Remove
              </Button>
            </Box>
          </Box>
        </Box>
      </ListItem>
    ),
    [handleAddToCart, handleRemoveFav, removing],
  );

  // Refresh cart function
  const handleRefreshCart = useCallback(() => {
    handleGetCart();

    refetch();
  }, [handleGetCart, refetch]);

  // Empty cart component
  const EmptyCart = ({ message }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        textAlign: "center",
        minHeight: "40vh",
      }}
    >
      <img
        src={emptyCart}
        alt="Empty cart"
        width={80}
        style={{ marginBottom: 16 }}
      />
      <Typography variant="h6" sx={{ mb: 1, fontSize: "16px" }}>
        {message}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Browse our products and add items to your wishlist
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          maxWidth: "200px",
        }}
      >
        <Button
          variant="contained"
          onClick={handleRefreshCart}
          startIcon={<SlRefresh />}
          sx={{
            backgroundColor: "#5C3069",
            "&:hover": {
              backgroundColor: "#4a2555",
            },
            borderRadius: "8px",
            px: 3,
            py: 1,
          }}
        >
          Refresh Wishlist
        </Button>
        <Button
          variant="outlined"
          onClick={handleContinueShopping}
          sx={{
            borderColor: "#5C3069",
            color: "#5C3069",
            "&:hover": {
              borderColor: "#4a2555",
              backgroundColor: "rgba(92, 48, 105, 0.04)",
            },
            borderRadius: "8px",
            px: 3,
            py: 1,
          }}
        >
          Continue Shopping
        </Button>
      </Box>
    </Box>
  );



  return (
    <>
      {/* Floating close button outside drawer */}
      {open && (
        <IconButton
          onClick={onClose}
          sx={{
            position: "fixed",
            top: 16,
            left: isMobile ? 16 : "calc(100vw - 450px - 60px)",
            zIndex: 1300, // Higher than drawer's z-index
            backgroundColor: "#fff",
            color: "white",
            width: 40,
            height: 40,
            "&:hover": {
              backgroundColor: "rgba(244, 238, 238, 0.9)",
            },
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
          }}
        >
          <CloseIcon fontSize="small" sx={{ color: "#000" }} />
        </IconButton>
      )}

      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: isMobile ? "100vw" : 450,
            maxWidth: "100vw",
            borderRadius: "21px",
            m: 1,
            pb: 1,
            bgcolor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Box
          sx={{
            height: "98%",
            display: "flex",
            bgcolor: "#fff",
            flexDirection: "column",
            borderRadius: "21px",
            overflow: "hidden",
            boxShadow: 4,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", columnGap: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: "24px" }}
              >
                My Wishlist
              </Typography>
              <Badge
                badgeContent={totals.totalItems}
                color="error"
                sx={{ ml: 1 }}
              />
            </Box>
            {/* Order  */}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography>Loading wishlist...</Typography>
              </Box>
            ) : (
              <>
                {/* Products Tab */}
                {tabValue === 0 && (
                  <>
                    {productCartItems.length === 0 ? (
                      <EmptyCart message="Your wishlist is empty" />
                    ) : (
                      <List sx={{ p: 0 }}>
                        {productCartItems.map((item) =>
                          renderCartItem(item, false),
                        )}
                      </List>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default WishlistDrawer;
